import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  X, 
  RefreshCw, 
  Activity,
  Calendar,
  MessageSquare,
  CheckCircle,
  XCircle,
  TrendingUp,
  User,
  Star,
  Phone,
  FileText,
  Edit
} from 'lucide-react';

const CandidateHistoryModal = ({ candidate, onClose, historyLoading, candidateHistory, fetchCandidateHistory }) => {
  const [processedHistory, setProcessedHistory] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  
  useEffect(() => {
    if (!candidateHistory || candidateHistory.length === 0) {
      setProcessedHistory([]);
      return;
    }

    const processed = [];
    const excludedFields = [
      // System timestamps
      'updated_at', 'created_at', 'id', 'candidate_id',
      'reporting_updated_at', 'project_updated_at', 'feedback_updated_at',
      'project_form_last_reminder_at', 'project_info_submitted_at',
      'project_form_sent_at', 'project_form_completed_at',
      'extension_data_cleared_at', 'sent_to_rd_date',
      'last_extension_cycle',
      // Internal tracking — who updated (shown in card header, not as a change)
      'reporting_updated_by', 'project_updated_by', 'feedback_updated_by',
      'extension_data_cleared_by',
      // System flags not meaningful to users
      'is_real_candidate', 'candidate_added_source',
      'extension_cycle_count', 'project_info_status',
      'project_form_reminder_count'
    ];
    
    candidateHistory.forEach(record => {
      if (record.old_values && record.new_values) {
        const oldValues = typeof record.old_values === 'string' 
          ? JSON.parse(record.old_values) 
          : record.old_values;
        const newValues = typeof record.new_values === 'string' 
          ? JSON.parse(record.new_values) 
          : record.new_values;

        const changes = [];
        Object.keys(newValues).forEach(fieldName => {
          if (excludedFields.includes(fieldName)) return;
          
          const oldValue = oldValues[fieldName];
          const newValue = newValues[fieldName];
          
          if (oldValue !== newValue && !(oldValue === null && newValue === null)) {
            changes.push({
              field_name: fieldName,
              old_value: oldValue,
              new_value: newValue
            });
          }
        });

        if (changes.length > 0) {
          processed.push({
            id: record.audit_id,
            changes: changes,
            category: determineCategory(changes),
            created_by: (() => {
            const raw = (record.action_by || record.created_by || '').trim();
            if (!raw || raw === 'root@localhost' || raw === 'root' || raw === 'Unknown User') {
              return 'System';
            }
            return raw;
          })(),
            created_at: record.action_at || record.created_at
          });
        }
      }
    });

    processed.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setProcessedHistory(processed);
  }, [candidateHistory]);

  const determineCategory = (changes) => {
    const fields = changes.map(c => c.field_name.toLowerCase());
    
    if (fields.some(f => ['extension_requested', 'extension_status', 'extension_date', 
         'extension_duration', 'extension_comments', 'no_extension_reason', 
         'sent_to_rd', 'extended_sow_date', 'poc_name', 'contacted_with'].includes(f))) {
      return 'extension';
    }
    
    if (fields.some(f => ['client_rating', 'client_feedback', 'improvement_area_1', 
         'improvement_area_2', 'improvement_area_3'].includes(f))) {
      return 'feedback';
    }
    
    if (fields.some(f => ['end_date', 'sow_end_date', 'contractor_status', 'status'].includes(f))) {
      return 'rolloff';
    }

    if (fields.some(f => ['project_tech_stack', 'project_overview', 'project_goals',
         'project_role_responsibility', 'key_achievement', 'project_experience_rating'].includes(f))) {
      return 'project';
    }

    if (fields.some(f => ['work_email', 'reporting_manager_name', 'reporting_manager_email',
         'reporting_manager_phone', 'reporting_manager_location', 'reporting_manager_linkedin',
         'reporting_manager_connected_date', 'reporting_comments',
         'candidate_connected_date', 'candidate_connected_comments',
         'client_tag_poc_name', 'client_tag_poc_email', 'client_tag_poc_phone',
         'client_tag_poc_location', 'client_tag_poc_linkedin',
         'client_tag_poc_connected_date', 'client_tag_poc_comments'].includes(f))) {
      return 'reporting';
    }
    
    return 'general';
  };

  const getCategoryConfig = (category) => {
    switch (category) {
      case 'extension':
        return {
          label: 'Extension Request',
          icon: Calendar,
          color: 'text-green-600',
          bg: 'bg-green-50',
          border: 'border-green-300',
          dotBg: 'bg-green-500'
        };
      case 'feedback':
        return {
          label: 'Client Feedback',
          icon: MessageSquare,
          color: 'text-orange-600',
          bg: 'bg-orange-50',
          border: 'border-orange-300',
          dotBg: 'bg-orange-500'
        };
      case 'rolloff':
        return {
          label: 'Roll-off Movement',
          icon: TrendingUp,
          color: 'text-red-600',
          bg: 'bg-red-50',
          border: 'border-red-300',
          dotBg: 'bg-red-500'
        };
      case 'project':
        return {
          label: 'Project Details',
          icon: FileText,
          color: 'text-indigo-600',
          bg: 'bg-indigo-50',
          border: 'border-indigo-300',
          dotBg: 'bg-indigo-500'
        };
      case 'reporting':
        return {
          label: 'Circle of Influence',
          icon: User,
          color: 'text-violet-600',
          bg: 'bg-violet-50',
          border: 'border-violet-300',
          dotBg: 'bg-violet-500'
        };
      default:
        return {
          label: 'Basic Update',
          icon: Edit,
          color: 'text-blue-600',
          bg: 'bg-blue-50',
          border: 'border-blue-300',
          dotBg: 'bg-blue-500'
        };
    }
  };

  const formatFieldName = (fieldName) => {
    const fieldMap = {
      'extension_requested': 'Extension Decision',
      'extension_status': 'Extension Status',
      'extension_duration': 'Extension Duration',
      'extension_comments': 'Extension Comments',
      'no_extension_reason': 'Not Extending Reason',
      'contacted_with': 'Contacted With',
      'poc_name': 'Client POC',
      'sent_to_rd': 'Sent to RD',
      'client_rating': 'Client Rating',
      'client_feedback': 'Feedback',
      'improvement_area_1': 'Improvement Area 1',
      'improvement_area_2': 'Improvement Area 2',
      'improvement_area_3': 'Improvement Area 3',
      'end_date': 'End Date',
      'sow_end_date': 'SOW End Date',
      'contractor_status': 'Contractor Status'
    };
    
    return fieldMap[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatValue = (value, fieldName) => {
    if (value === null || value === undefined) return 'Not Set';
    if (value === '') return 'Empty';
    
    if (fieldName === 'extension_requested') {
      if (value === 1 || value === '1') return '✓ Extended';
      if (value === 2 || value === '2') return '✗ Not Extended';
      return 'Pending';
    }
    
    if (fieldName === 'sent_to_rd') {
      return value === 1 || value === '1' ? 'Yes' : 'No';
    }
    
    if (fieldName === 'client_rating') {
      return `${value}/5 ⭐`;
    }
    
    return String(value);
  };

  const getFieldIcon = (fieldName) => {
    const field = fieldName.toLowerCase();
    if (field.includes('extension') || field.includes('date')) return Calendar;
    if (field.includes('feedback') || field.includes('rating') || field.includes('improvement')) return MessageSquare;
    if (field.includes('contacted') || field.includes('poc')) return Phone;
    if (field.includes('status')) return Activity;
    return FileText;
  };

  // Filter history by active category
  const filteredHistory = activeCategory === 'all' 
    ? processedHistory 
    : processedHistory.filter(record => record.category === activeCategory);

  // Count by category
  const categoryCounts = {
    all: processedHistory.length,
    general: processedHistory.filter(r => r.category === 'general').length,
    extension: processedHistory.filter(r => r.category === 'extension').length,
    feedback: processedHistory.filter(r => r.category === 'feedback').length,
    rolloff: processedHistory.filter(r => r.category === 'rolloff').length,
    project: processedHistory.filter(r => r.category === 'project').length,
    reporting: processedHistory.filter(r => r.category === 'reporting').length,
  };

  const categories = [
    { id: 'all', label: 'All Updates', icon: Activity, count: categoryCounts.all },
    { id: 'general', label: 'Basic Updates', icon: Edit, count: categoryCounts.general },
    { id: 'reporting', label: 'Circle of Influence', icon: User, count: categoryCounts.reporting },
    { id: 'project', label: 'Project Updates', icon: FileText, count: categoryCounts.project },
    { id: 'extension', label: 'Extension Updates', icon: Calendar, count: categoryCounts.extension },
    { id: 'feedback', label: 'Client Feedback', icon: MessageSquare, count: categoryCounts.feedback },
    { id: 'rolloff', label: 'Roll-off Updates', icon: TrendingUp, count: categoryCounts.rolloff },
  ];

  // Group changes by category
  const extensionChanges = (changes) => changes.filter(c => 
    ['extension_requested', 'extension_status', 'extension_duration', 'extension_comments', 
     'no_extension_reason', 'contacted_with', 'poc_name', 'sent_to_rd', 'extended_sow_date'].includes(c.field_name.toLowerCase())
  );
  
  const feedbackChanges = (changes) => changes.filter(c => 
    ['client_rating', 'client_feedback', 'improvement_area_1', 'improvement_area_2', 'improvement_area_3'].includes(c.field_name.toLowerCase())
  );
  
  const contractChanges = (changes) => changes.filter(c => 
    ['end_date', 'sow_end_date', 'contractor_status', 'status'].includes(c.field_name.toLowerCase())
  );

  const renderChangeGroup = (title, changes, iconComponent, colorClasses) => {
    if (changes.length === 0) return null;
    
    const Icon = iconComponent;
    
    return (
      <div className="mb-3">
        <div className="flex items-center space-x-2 mb-2">
          <Icon className={`w-4 h-4 ${colorClasses}`} />
          <h5 className={`text-xs font-bold uppercase tracking-wide ${colorClasses}`}>{title}</h5>
        </div>
        <div className="space-y-2 ml-6">
          {changes.map((change, idx) => {
            const FieldIcon = getFieldIcon(change.field_name);
            return (
              <div key={idx} className="flex items-start space-x-2 text-xs">
                <FieldIcon className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-slate-700">{formatFieldName(change.field_name)}:</span>
                  {change.old_value && (
                    <span className="text-red-600 line-through mx-1">{formatValue(change.old_value, change.field_name)}</span>
                  )}
                  <span className="text-green-600 font-medium">→ {formatValue(change.new_value, change.field_name)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] overflow-hidden flex">
        {/* Left Sidebar - Categories */}
        <div className="w-64 bg-gradient-to-b from-slate-50 to-slate-100 border-r border-slate-200 flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-slate-200 bg-white">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Categories</h3>
            <p className="text-xs text-slate-500 mt-1">{candidate?.candidateName}</p>
          </div>

          {/* Category List */}
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {categories.map((cat) => {
              const CategoryIcon = cat.icon;
              const isActive = activeCategory === cat.id;
              
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-all text-left ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-slate-700 hover:bg-white hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <CategoryIcon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                    <span className="text-sm font-medium">{cat.label}</span>
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-200 text-slate-600'
                  }`}>
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Sidebar Footer */}
          <div className="p-3 border-t border-slate-200 bg-white">
            <div className="text-xs text-slate-500">
              <div className="flex items-center justify-between mb-1">
                <span>Total Activities:</span>
                <span className="font-bold text-slate-900">{processedHistory.length}</span>
              </div>
              {processedHistory.length > 0 && (
                <div className="flex items-center justify-between">
                  <span>Latest:</span>
                  <span className="font-semibold text-slate-700">
                    {new Date(processedHistory[0].created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Content - Timeline */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-3 text-white flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Activity Pipeline</h2>
                  <p className="text-blue-100 text-xs">
                    {activeCategory === 'all' ? 'All Updates' : categories.find(c => c.id === activeCategory)?.label} • {filteredHistory.length} record{filteredHistory.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => fetchCandidateHistory(candidate.id)}
                  disabled={historyLoading}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${historyLoading ? 'animate-spin' : ''}`} />
                </button>
                <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Timeline Content */}
          <div className="flex-1 overflow-y-auto p-5 bg-slate-50">
            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400 mr-3" />
                <span className="text-slate-500 text-sm">Loading history...</span>
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-semibold text-slate-700 mb-1">No Activities Found</p>
                <p className="text-xs text-slate-500">
                  {activeCategory === 'all' 
                    ? 'No activity history available'
                    : `No ${categories.find(c => c.id === activeCategory)?.label.toLowerCase()} recorded`
                  }
                </p>
              </div>
            ) : (
              <div className="relative">
                {/* Vertical Timeline Line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 via-purple-200 to-pink-200"></div>
                
                {/* Timeline Items */}
                <div className="space-y-4">
                  {filteredHistory.map((record, index) => {
                    const config = getCategoryConfig(record.category);
                    const CategoryIcon = config.icon;
                    
                    const extChanges = extensionChanges(record.changes);
                    const fbChanges = feedbackChanges(record.changes);
                    const ctChanges = contractChanges(record.changes);
                    
                    return (
                      <div key={record.id} className="relative pl-11">
                        {/* Timeline Dot */}
                        <div className={`absolute left-2.5 top-2 w-3 h-3 rounded-full ${config.dotBg} ring-4 ring-white shadow-sm`}></div>
                        
                        {/* Card */}
                        <div className={`bg-white border-2 ${config.border} rounded-xl p-4 hover:shadow-md transition-shadow`}>
                          {/* Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <div className={`w-8 h-8 rounded-lg ${config.bg} border ${config.border} flex items-center justify-center`}>
                                <CategoryIcon className={`w-4 h-4 ${config.color}`} />
                              </div>
                              <div>
                                <h4 className={`font-bold text-sm ${config.color}`}>{config.label}</h4>
                                <p className="text-xs text-slate-500">
                                  {record.changes.length} change{record.changes.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center space-x-1 text-xs text-slate-600">
                                <User className="w-3 h-3" />
                                <span className={`font-medium ${record.created_by === 'System' ? 'text-slate-400 italic' : 'text-slate-700'}`}>
                                  {record.created_by}
                                </span>
                              </div>
                              <div className="flex items-center space-x-1 text-xs text-slate-500 mt-0.5">
                                <Clock className="w-3 h-3" />
                                <span>
                                  {new Date(record.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                                <span>•</span>
                                <span>
                                  {new Date(record.created_at).toLocaleTimeString('en-US', {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Changes by Category */}
                          <div className="space-y-3">
                            {renderChangeGroup('Extension Details', extChanges, Calendar, 'text-green-600')}
                            {renderChangeGroup('Client Feedback', fbChanges, MessageSquare, 'text-orange-600')}
                            {renderChangeGroup('Contract Status', ctChanges, TrendingUp, 'text-red-600')}
                            
                            {/* Other changes not categorized */}
                            {record.changes.filter(c => 
                              ![...extChanges, ...fbChanges, ...ctChanges].includes(c)
                            ).length > 0 && renderChangeGroup(
                              'Other Changes', 
                              record.changes.filter(c => ![...extChanges, ...fbChanges, ...ctChanges].includes(c)),
                              FileText,
                              'text-blue-600'
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateHistoryModal;